import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { coursesService } from '../services/courses.service';
import { supabase } from '../lib/supabaseClient';
import { Course, Lesson } from '../types/courses.types';
import { useFeatures } from '../hooks/useFeatures';
import { useAuthStore } from '../store/authStore';
import '../styles/courses.css';

const DIFFICULTY_LABEL: Record<string,string> = { beginner:'Başlangıç', intermediate:'Orta', advanced:'İleri' };

export function CourseDetailPage() {
  const { courseId } = useParams<{ courseId: string }>();
  const [course, setCourse] = useState<Course | null>(null);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [completedLessons, setCompletedLessons] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { hasFeature } = useFeatures();
  const profile = useAuthStore((s) => s.profile);

  useEffect(() => {
    if (!courseId) return;
    let cancelled = false;

    async function fetchData() {
      setIsLoading(true);
      try {
        // TÜM sorguları PARALEL çalıştır (waterfall kaldırıldı)
        const [courseData, lessonsData, completionsData] = await Promise.all([
          coursesService.getCourse(courseId!),
          coursesService.listLessons(courseId!),
          // Tamamlama verisi de paralel çekilir (profile varsa)
          profile
            ? supabase
                .from('student_lesson_completions')
                .select('lesson_id')
                .eq('student_id', profile.id)
                .then(({ data }) => data)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        if (!courseData) { setError('Kurs bulunamadı.'); return; }

        // Yaş grubu yetki doğrulaması
        const userAge = profile?.age;
        if (userAge && courseData.age_category) {
          let blocked = false;
          if (courseData.age_category === '7-9' && (userAge < 7 || userAge > 9)) blocked = true;
          else if (courseData.age_category === '10-13' && (userAge < 10 || userAge > 13)) blocked = true;
          else if (courseData.age_category === '14-17' && (userAge < 14 || userAge > 17)) blocked = true;

          if (blocked) {
            setError(`Bu kurs sadece ${courseData.age_category} yaş grubu içindir. Sizin yaşınız (${userAge}) bu kurs için uygun değildir.`);
            setIsLoading(false);
            return;
          }
        }

        setCourse(courseData);
        setLessons(lessonsData);

        // Tamamlama verisi — sadece bu kurstaki dersler için filtrele
        if (completionsData && lessonsData.length > 0) {
          const lessonIds = new Set(lessonsData.map((l) => l.id));
          const completedInCourse = completionsData
            .filter((c: any) => lessonIds.has(c.lesson_id))
            .map((c: any) => c.lesson_id);
          setCompletedLessons(new Set(completedInCourse));
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Kurs yüklenemedi.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetchData();
    return () => { cancelled = true; };
  }, [courseId, profile?.id]);

  // Realtime: Öğrenci bir dersi tamamladığında progress bar'ı anlık güncelle
  useEffect(() => {
    if (!courseId || !profile?.id) return;

    const channel = supabase
      .channel(`course-progress-${courseId}-${profile.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'student_lesson_completions',
          filter: `student_id=eq.${profile.id}`,
        },
        (payload) => {
          const newLessonId = payload.new?.lesson_id as string | undefined;
          if (newLessonId) {
            setCompletedLessons((prev) => new Set([...prev, newLessonId]));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [courseId, profile?.id]);

  if (isLoading) return <div className="courses-loading">Yükleniyor...</div>;
  if (error) return <div className="courses-error">{error}</div>;
  if (!course) return null;

  const isAdmin = profile?.role === 'admin';
  const canAccessPremium = isAdmin || hasFeature('lesson.premium') || hasFeature('course.premium');
  const completedCount = completedLessons.size;
  const totalCount = lessons.length;
  const overallPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div className="course-detail-page">
      <Link to="/courses" className="course-detail-back">⬅️ Derslere Dön</Link>

      <header className="course-detail-header">
        <h1>{course.title}</h1>
        {course.description && <p style={{ whiteSpace: 'pre-wrap' }}>{course.description}</p>}
        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', marginTop:8 }}>
          <span className="course-detail-difficulty">🎯 {DIFFICULTY_LABEL[course.difficulty] ?? course.difficulty}</span>
          {(course as any).is_premium && <span className="lesson-badge" style={{ background:'rgba(99,102,241,0.15)', color:'var(--primary-light)' }}>⭐ Premium Kurs</span>}
          {(course as any).certificate_enabled && <span className="lesson-badge" style={{ background:'rgba(16,185,129,0.15)', color:'var(--secondary)' }}>🎓 Sertifika</span>}
        </div>
      </header>

      {/* Course Progress Bar */}
      {totalCount > 0 && (
        <div className="card" style={{ marginBottom: 20, padding: '16px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>Kurs İlerleme</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{completedCount}/{totalCount} ders tamamlandı — %{overallPercent}</span>
          </div>
          <div className="progress-bar">
            <div className="progress-bar-fill" style={{ width: `${overallPercent}%` }} />
          </div>
        </div>
      )}

      {lessons.length === 0 ? (
        <div className="courses-empty">Bu kurs için henüz ders eklenmemiş.</div>
      ) : (
        <div className="course-detail-lesson-list">
          {lessons.map((lesson, i) => {
            const l = lesson as any;
            const isPreview = l.is_preview === true;
            const isPremium = l.is_premium === true;
            const locked = isPremium && !isPreview && !canAccessPremium;
            const isCompleted = completedLessons.has(lesson.id);

            return locked ? (
              <Link to="/pricing" key={lesson.id} className="course-detail-lesson-item" style={{ opacity: 0.75 }}>
                <div className="course-detail-lesson-number" style={{ background: 'var(--bg-alt)', color: 'var(--text-muted)', borderColor: 'var(--border)' }}>{i+1}</div>
                <div className="course-detail-lesson-info">
                  <h4 style={{ color: 'var(--text-muted)' }}>{lesson.title}</h4>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 700 }}>🔒 Premium</span>
                  <div className="course-detail-lesson-xp">+{lesson.xp_reward} XP</div>
                </div>
              </Link>
            ) : (
              <Link
                to={`/lessons/${lesson.id}`}
                key={lesson.id}
                className={`course-detail-lesson-item ${isCompleted ? 'lesson-completed' : ''}`}
              >
                <div 
                  className="course-detail-lesson-number" 
                  style={isCompleted ? { background: '#10b981', color: '#fff', borderColor: '#10b981' } : undefined}
                >
                  {isCompleted ? '✓' : i+1}
                </div>
                {lesson.image_url && (
                  <img 
                    src={lesson.image_url} 
                    alt={lesson.title} 
                    style={{ width: 48, height: 48, borderRadius: 10, objectFit: 'cover', flexShrink: 0, border: '1px solid var(--border)' }} 
                  />
                )}
                <div className="course-detail-lesson-info">
                  <h4 style={isCompleted ? { color: '#10b981' } : undefined}>{lesson.title}</h4>
                  {isCompleted && <span style={{ fontSize: 11, color: '#10b981', fontWeight: 600 }}>Tamamlandı</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {isPreview && <span style={{ fontSize: 12, color: 'var(--secondary)', fontWeight: 700 }}>👁️ Ücretsiz</span>}
                  {isPremium && canAccessPremium && <span style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 700 }}>⭐</span>}
                  <div className="course-detail-lesson-xp">+{lesson.xp_reward} XP</div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {(course as any).is_premium && !canAccessPremium && (
        <div className="card" style={{ marginTop: 24, textAlign: 'center', padding: '24px 32px', border: '1px solid rgba(99,102,241,0.2)' }}>
          <p style={{ color: 'var(--text-muted)', marginBottom: 12 }}>Bu kursa tam erişmek için Premium planına geç.</p>
          <Link to="/pricing" className="lesson-btn primary">💎 Premium'a Geç — ₺499/ay</Link>
        </div>
      )}
    </div>
  );
}
